<template>
  <v-dialog v-model="dialog" max-width="1000">
    <v-card>
      <v-card-title class="headline">
        Actualizar Código de Servicio de Miembros
      </v-card-title>
      
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-row>
              <v-col cols="12">
                <v-select
                  :items="groupOptions"
                  label="Grupo"
                  v-model="selectedGroup"
                  @change="onGroupChange"
                  required
                  outlined
                  dense
                  color="primary"
                  item-color="primary"
                ></v-select>
              </v-col>

              <v-col cols="12">
                <v-select
                  :items="categoryOptions"
                  label="Categoría"
                  v-model="selectedCategory"
                  @change="calculateServiceCodeSummary"
                  multiple
                  outlined
                  dense
                  color="primary"
                  item-color="primary"
                ></v-select>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  label="Código de Servicio"
                  v-model="selectedServiceCode"
                  @input="calculateServiceCodeSummary"
                  required
                  outlined
                  dense
                  color="primary"
                  :rules="[v => !!v || 'El código de servicio es requerido']"
                  hint="Ingresa el código de servicio que se asignará a los miembros del grupo"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <v-col cols="12" v-if="selectedServiceCode && selectedGroup">
                <v-alert
                  type="info"
                  dense
                  text
                  border="left"
                  color="primary"
                  class="mb-2"
                >
                  <span class="text-body-2">
                    Se actualizará el código de servicio a <strong>{{ selectedServiceCode }}</strong> 
                    para el grupo <strong>{{ getGroupText(selectedGroup) }}</strong>
                    <span v-if="selectedCategory.length > 0">
                      y categoría(s): <strong>{{ getCategoryText() }}</strong>
                    </span>
                  </span>
                </v-alert>
              </v-col>
            </v-row>
          </v-col>

          <!-- Panel de Resumen -->
          <v-col cols="12" md="6" v-if="selectedServiceCode && selectedGroup">
            <v-card outlined class="pa-4 elevation-2" color="grey lighten-5">
              <v-card-title class="pa-0 mb-3">
                <h4 class="primary--text">Resumen de Actualización</h4>
              </v-card-title>
              <v-card-text class="pa-0">
                <div class="mb-3">
                  <div class="text-body-2 grey--text text--darken-2 mb-1">Grupo Seleccionado:</div>
                  <div class="text-body-1 font-weight-medium">{{ getGroupText(selectedGroup) }}</div>
                </div>
                
                <div class="mb-3" v-if="selectedCategory.length > 0">
                  <div class="text-body-2 grey--text text--darken-2 mb-1">Categoría(s) Seleccionada(s):</div>
                  <div class="text-body-1 font-weight-medium">{{ getCategoryText() }}</div>
                </div>
                
                <div class="mb-3">
                  <div class="text-body-2 grey--text text--darken-2 mb-1">Nuevo Código de Servicio:</div>
                  <div class="text-body-1 font-weight-medium primary--text">{{ selectedServiceCode }}</div>
                </div>

                <v-divider class="my-3"></v-divider>
                
                <div class="text-caption grey--text text--darken-1">
                  Esta acción afectará a todos los miembros del grupo seleccionado.
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          text
          @click="closeDialog"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="updateServiceCode"
          :disabled="!selectedServiceCode || !selectedGroup || loading"
          :loading="loading"
        >
          Actualizar Código de Servicio
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'UpdateServiceCodeDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    userPermissions: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedGroup: null,
      selectedCategory: [],
      selectedServiceCode: null,
      loading: false,
      categoryOptions: [
        { text: 'TODOS', value: 'ALL' },
        { text: 'Asociados Temporales', value: 'TEMP' },
        { text: 'Asociados Vitalicios', value: 'VITAL' },
        { text: 'Asociados Honorarios Vitalicios', value: 'HONOR_VITAL' },
        { text: 'Reconocimiento por participacion', value: 'RECOGNITION' },
        { text: 'Ejecutivo de Finanzas', value: 'FINANCE_EXEC' },
        { text: 'Socios con Beneficio', value: 'BENEFIT_PARTNER' },
        { text: 'Nueva Generacion', value: 'NEW_GEN' },
        { text: 'Socio a distancia', value: 'REMOTE_PARTNER' }
      ],
      groups: [
        'CIUDAD DE MEXICO',
        'MONTERREY',
        'ESTADO DE MEXICO',
        'GUADALAJARA',
        'LAGUNA',
        'LA LAGUNA',
        'COAHUILA',
        'MORELOS',
        'PUEBLA',
        'TAMAULIPAS',
        'QUERETARO',
        'CIUDAD JUAREZ',
        'CHIAPAS',
        'CHIHUAHUA',
        'GUANAJUATO',
        'CABOS',
        'CANCUN',
        'SAN LUIS POTOSI',
        'SINALOA',
        'SONORA',
        'VERACRUZ',
        'YUCATAN'
      ]
    };
  },
  
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    
    groupOptions() {
      const options = [{ text: 'TODOS', value: 'ALL' }];
      
      this.groups.forEach(city => {
        options.push({
          text: city,
          value: city
        });
      });
      
      return options;
    }
  },
  
  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
    selectedCategory(newVal, oldVal) {
      // Si se selecciona "TODOS", seleccionar todas las otras categorías
      if (newVal.includes('ALL') && !oldVal.includes('ALL')) {
        this.selectedCategory = this.categoryOptions
          .filter(category => category.value !== 'ALL')
          .map(category => category.value);
        this.calculateServiceCodeSummary();
      }
      // Si se deselecciona alguna categoría individual, quitar "TODOS" si estaba
      else if (oldVal.includes('ALL') && !newVal.includes('ALL')) {
        // No hacer nada, permitir la deselección individual
      }
    }
  },
  
  methods: {
    onGroupChange() {
      this.calculateServiceCodeSummary();
    },
    
    getGroupText(value) {
      if (value === 'ALL') return 'TODOS';
      return value; // Ahora value es directamente el nombre de la ciudad
    },
    
    getCategoryText() {
      if (this.selectedCategory.length === 0) return 'Ninguna';
      return this.selectedCategory
        .map(catValue => {
          const option = this.categoryOptions.find(opt => opt.value === catValue);
          return option ? option.text : catValue;
        })
        .join(', ');
    },
    
    calculateServiceCodeSummary() {
      // Esta función puede expandirse para mostrar información adicional
      // como el número estimado de miembros que serán afectados
    },
    
    async updateServiceCode() {
      if (!this.selectedServiceCode || !this.selectedGroup) {
        this.$emit('error', 'Por favor complete todos los campos requeridos');
        return;
      }

      this.loading = true;

      try {
        // Endpoint para actualizar códigos de servicio
        const response = await this.axios.post('/members/edit-service-codes', {
          group: this.selectedGroup,
          categories: this.selectedCategory.length > 0 ? this.selectedCategory : null,
          serviceCode: this.selectedServiceCode
        });

        if (response.status === 200) {
          this.$emit('success', {
            message: `Se actualizó el código de servicio exitosamente`
          });
          this.closeDialog();
        }
      } catch (error) {
        console.error('Error updating service code:', error);
        this.$emit('error', 'Error al actualizar el código de servicio. Intente nuevamente.');
      } finally {
        this.loading = false;
      }
    },
    
    resetForm() {
      this.selectedGroup = null;
      this.selectedCategory = [];
      this.selectedServiceCode = null;
    },
    
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    }
  }
};
</script>

<style scoped>
.v-card-title {
  word-break: break-word;
}
</style>